import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UsageAnalytics } from "@/components/UsageAnalytics";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { DataProvider } from "@/context/DataContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import Employees from "@/pages/Employees";
import Departments from "@/pages/Departments";
import Managers from "@/pages/Managers";
import ControlPanel from "@/pages/ControlPanel";
import EmployeeTimeline from "@/pages/EmployeeTimeline";
import LearningHubSwitch from "@/pages/LearningHubSwitch";
import Attendance from "@/pages/Attendance";
import KnoAttendance from "@/pages/KnoAttendance";
import KnoCheckIn from "@/pages/KnoCheckIn";
import OrgChart from "@/pages/OrgChart";
import News from "@/pages/News";
import ActivityPage from "@/pages/Activity";
import MarketResearch from "@/pages/MarketResearch";
import Projects from "@/pages/Projects";
import LearningDepartmentAll from "@/pages/LearningDepartmentAll";
import LearningDepartmentPage from "@/pages/LearningDepartmentPage";

function AppRouter() {
    const location = useLocation();
    // CRITICAL: Check URL fragment for session_id during render (avoids race)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="org" element={<OrgChart />} />
                <Route path="roa" element={<Navigate to="/calendar?type=roa" replace />} />
                <Route path="employees" element={<Employees />} />
                <Route path="employees/:id" element={<EmployeeTimeline />} />
                <Route path="departments" element={<Departments />} />
                <Route path="managers" element={<Managers />} />
                <Route path="learning" element={<LearningHubSwitch />} />
                <Route path="learning/:sub" element={<LearningHubSwitch />} />
                <Route path="learning/department/:deptId" element={<LearningDepartmentPage />} />
                <Route path="learning/department-legacy/:deptId" element={<LearningDepartmentAll />} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="kno" element={<KnoAttendance />} />
                <Route path="kno/check-in" element={<KnoCheckIn />} />
                <Route path="news" element={<News />} />
                <Route path="activity" element={<ActivityPage />} />
                <Route path="projects" element={<Projects />} />
                <Route path="research" element={<MarketResearch />} />
                <Route path="profile" element={<MyTimelineRedirect />} />
                <Route path="usage" element={<UsageAnalytics />} />
                <Route
                    path="control-panel"
                    element={
                        <ProtectedRoute roles={["super_admin", "manager"]}>
                            <ControlPanel />
                        </ProtectedRoute>
                    }
                />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

const MyTimelineRedirect = () => {
    const { user } = useAuth();
    return <Navigate to={user?.user_id ? `/employees/${user.user_id}` : "/"} replace />;
};

function App() {
    React.useEffect(() => {
        const splash = document.getElementById("initial-splash");
        if (!splash) return;
        // Give the bundle a tick to paint, then fade out.
        const t = setTimeout(() => {
            splash.classList.add("hide");
            setTimeout(() => splash.remove(), 400);
        }, 250);
        return () => clearTimeout(t);
    }, []);
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <DataProvider>
                        <AppRouter />
                        <Toaster richColors position="top-right" />
                    </DataProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
